import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, GraduationCap, MapPin, Calendar, BookOpen, 
  Receipt, Settings, Euro,
  UserCheck, Clock, AlertCircle, Bell, Search,
  Upload, Download, CreditCard, Building, DoorOpen,
  CalendarDays, TrendingUp, FileSpreadsheet, ClipboardList,
  UserPlus, MessagesSquare, CheckSquare, Star, Laptop, UsersRound,
  Filter, FilterX, Smartphone, LayoutGrid, Move
} from 'lucide-react';
import api from '@/lib/axios';
import Layout from '@/components/layout/Layout';
import { useActionBarConfig } from '@/contexts/ActionBarContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import RegistrationsTrackerWidget from '@/pages/Dashboard/RegistrationsTrackerWidget';
import SmsDeliveryWidget from '@/pages/Dashboard/SmsDeliveryWidget';
import DataStatusWidget from '@/pages/Dashboard/DataStatusWidget';
import HomeOpsBoard from '@/pages/Dashboard/HomeOpsBoard';
import { StartfensterCustomizeDialog } from '@/pages/Dashboard/StartfensterCustomizeDialog';
import { useStartfensterLayout } from '@/pages/Dashboard/useStartfensterLayout';
import { SortableItem } from '@/pages/Dashboard/SortableItem';
import { TILE_SECTION_META } from '@/pages/Dashboard/startfensterConfig';
import { getStartfensterAccess } from '@/pages/Dashboard/startfensterAccess';
import { LiveSearchDropdown } from '@/pages/Teaching/components/LiveSearchDropdown';
import { QuicklinksEditDialog, useQuicklinks } from '@/pages/Teaching/components/Quicklinks';
import { Link2 } from 'lucide-react';
import { getFooterSettings, footerToParams, setFooterSettings } from '@/components/FooterDateBar';
import { useTenant } from '@/contexts/TenantContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const WidgetButton = ({ icon: Icon, label, onClick, badge, badgeColor = 'red', description, badgeOnClick, badgeTitle }) => (
  <button
    onClick={onClick}
    className="group flex flex-col items-center justify-center p-3 sm:p-4 bg-white rounded-xl border border-brand-border hover:border-brand-red/40 hover:shadow-md transition-all duration-200 min-h-[110px] sm:min-h-[120px] w-full min-w-0 overflow-hidden relative"
    data-testid={`widget-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {badge && (
      badgeOnClick ? (
        <span
          title={badgeTitle || 'Direkt zur gefilterten Liste'}
          onClick={(e) => { e.stopPropagation(); badgeOnClick(); }}
          className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${
            badgeColor === 'red' ? 'bg-brand-red text-white hover:ring-brand-red/30' :
            badgeColor === 'green' ? 'bg-brand-success text-white hover:ring-brand-success/30' :
            'bg-brand-red text-white hover:ring-brand-red/30'
          }`}
          data-testid={`widget-badge-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {badge}
        </span>
      ) : (
        <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs font-bold rounded-full ${
          badgeColor === 'red' ? 'bg-brand-red text-white' :
          badgeColor === 'green' ? 'bg-brand-success text-white' :
          'bg-brand-red text-white'
        }`}>
          {badge}
        </span>
      )
    )}
    <div className="p-3 rounded-xl bg-brand-red/5 group-hover:bg-brand-red/10 transition-colors mb-2 shrink-0">
      <Icon className="w-7 h-7 text-brand-red" />
    </div>
    <span className="text-xs font-medium text-brand-text text-center leading-tight w-full px-0.5 break-words hyphens-auto">
      {label}
    </span>
    {description && (
      <span className="text-[10px] text-brand-muted mt-1 w-full px-0.5 text-center break-words">{description}</span>
    )}
  </button>
);

const WidgetSection = ({ title, children }) => {
  const items = React.Children.toArray(children);
  if (items.length === 0) return null;
  return (
    <div className="mb-6">
      {title && (
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-1">
          {title}
        </h3>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {items}
      </div>
    </div>
  );
};

export default function StartScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can, loaded: permsLoaded, isSuperAdmin, isLegacyFallback } = usePermissions();
  const { activeTenantId, loading: tenantLoading } = useTenant();
  const roleNames = (user?.roles || []).map((role) => role.name);
  const {
    isTeacher,
    isAdminish,
    showDataQuality,
    showAdminDashboardWidgets,
    showRegistrationsTracker,
  } = getStartfensterAccess(roleNames, { isSuperAdmin, isLegacyFallback });
  // RBAC: Kachel/KPI nur zeigen, wenn mind. Leserecht auf der Ressource
  const A = (res) => permsLoaded && can(res, 'read');
  const canStudents = A('students');
  const canReports = A('reports');
  const canInvoices = A('invoices');
  const canLessons = A('lessons');
  const canTeachers = A('teachers');
  const canCourses = A('courses');
  const canLocations = A('locations');
  const canFamilies = A('families');
  const canEnrollments = A('enrollments');

  const todayIsoLocal = () => {
    const n = new Date();
    const p = (x) => String(x).padStart(2, '0');
    return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
  };

  const openTodayCalendar = () => {
    navigate('/calendar?date=today&view=resources-vertical&rooms=all&location=all&begin=7');
  };
  const [footer, setFooter] = useState(getFooterSettings);
  // Anzeige: gefilterte KPIs wenn Fußleisten-Filter aktiv (wie iMikel)
  const showActiveOnly = footer.nurAktive || footer.stichtagOn || footer.zeitraumOn;
  const { quicklinks, save: saveQuicklinks } = useQuicklinks();
  const [quicklinksOpen, setQuicklinksOpen] = useState(false);
  const {
    hidden, isVisible, save: saveStartfenster,
    order, tileOrder, moveBlock, moveTile, resetOrder,
  } = useStartfensterLayout();
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const goToUV = (params) => navigate(`/unterrichtsverwaltung?${params}`);
  const [stats, setStats] = useState({
    students: 0,
    studentsTotal: 0,
    teachers: 0,
    teachersTotal: 0,
    courses: 0,
    coursesTotal: 0,
    locations: 0,
    locationsTotal: 0,
    rooms: 0,
    families: 0,
    enrollments: 0,
    openInvoices: 0,
    pendingEnrollments: 0,
    todayLessons: 0,
    conflicts: 0,
    teacherId: null,
  });
  useActionBarConfig({
    title: 'Startfenster',
    center: A('enrollments') ? (
      <div className="relative w-full max-w-md">
        <div className="flex">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted pointer-events-none" />
            <input
              type="text"
              placeholder="Alles durchsuchen..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setShowSearchDropdown(true); }}
              onFocus={() => searchTerm.length >= 2 && setShowSearchDropdown(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); setShowSearchDropdown(true); }
              }}
              className="w-full bg-white border border-brand-border border-r-0 rounded-l-lg pl-9 pr-3 py-1.5 text-sm text-brand-text placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red/40"
              data-testid="global-search-input"
            />
          </div>
          <button
            type="button"
            title="Suchen"
            onClick={() => setShowSearchDropdown(true)}
            className="shrink-0 px-2.5 rounded-r-lg bg-brand-red text-white hover:bg-brand-red/90 focus:outline-none focus:ring-2 focus:ring-brand-red/30"
            data-testid="global-search-submit"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
        <LiveSearchDropdown
          searchTerm={searchTerm}
          onSelect={(s) => goToUV(`student_id=${s.id}`)}
          onSelectCourse={(c) => goToUV(`course_id=${c.id}`)}
          onSelectTeacher={(t) => goToUV(`teacher_id=${t.id}`)}
          onSelectGuardian={(g) => goToUV(`guardian_id=${g.id}`)}
          isOpen={showSearchDropdown}
          onClose={() => setShowSearchDropdown(false)}
        />
      </div>
    ) : null,
  }, [searchTerm, showSearchDropdown, permsLoaded, can]);

  const stichtagLabel = (() => {
    let iso = footer.stichtagOn && footer.stichtag ? footer.stichtag : null;
    if (!iso) {
      const n = new Date();
      const p = (x) => String(x).padStart(2, '0');
      iso = `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
    }
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  })();

  useEffect(() => {
    const h = (e) => { if (e.detail) setFooter(e.detail); };
    window.addEventListener('footer-settings-changed', h);
    return () => window.removeEventListener('footer-settings-changed', h);
  }, []);

  useEffect(() => {
    // Wait for permissions + active tenant (X-Tenant-ID). After login, perms can
    // briefly still be the empty logged-out set — re-run when can* flips true.
    if (!permsLoaded || tenantLoading) return;
    if (!activeTenantId && !isSuperAdmin) return;
    loadStats(footerToParams(footer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permsLoaded, tenantLoading, activeTenantId, footer, canStudents, canReports, canInvoices, canLessons, canTeachers, canCourses, canLocations, canFamilies, isSuperAdmin]);

  const loadStats = async (params = {}) => {
    try {
      if (isTeacher) {
        const { data } = await api.get('/teacher/dashboard');
        setStats((current) => ({
          ...current,
          students: data.students_active || 0,
          studentsTotal: data.students_active || 0,
          courses: data.courses_active || 0,
          coursesTotal: data.courses_active || 0,
          enrollments: data.enrollments_active || 0,
          todayLessons: data.lessons_today || 0,
          teacherId: data.teacher?.id || user?.teacher_id || null,
        }));
        return;
      }
      // RBAC: nur Endpoints anfragen, für die Leserechte bestehen (keine 403-Probes)
      const empty = (data) => Promise.resolve({ data });
      const today = todayIsoLocal();
      const [
        studentsRes, kpisRes, invoicesRes, todayPlanRes,
        teachersRes, coursesRes, locationsRes, familiesRes,
      ] = await Promise.all([
        canStudents
          ? api.get('/students/stats/count', { params }).catch(() => ({ data: { total: 0, with_enrollment: 0 } }))
          : empty({ total: 0, with_enrollment: 0 }),
        canReports
          ? api.get('/reports/kpis', { params }).catch(() => ({ data: { teachers_total: 0, teachers_active: 0, courses_active: 0, courses_with_enrollment: 0, enrollments_active: 0, lessons_scheduled_in_range: 0 } }))
          : empty({ teachers_total: 0, teachers_active: 0, courses_active: 0, courses_with_enrollment: 0, enrollments_active: 0, lessons_scheduled_in_range: 0 }),
        canInvoices
          ? api.get('/invoices/stats/summary').catch(() => ({ data: { pending_count: 0 } }))
          : empty({ pending_count: 0 }),
        canLessons
          ? api.get('/lessons/plan-range', {
              params: { start_date: today, end_date: today, include_students: false },
            }).catch(() => ({ data: { entries: [] } }))
          : empty({ entries: [] }),
        canTeachers
          ? api.get('/teachers/stats/count').catch(() => ({ data: { total: 0, active: 0 } }))
          : empty({ total: 0, active: 0 }),
        canCourses
          ? api.get('/courses/stats/count').catch(() => ({ data: { total: 0, active: 0 } }))
          : empty({ total: 0, active: 0 }),
        canLocations
          ? api.get('/locations/stats/count').catch(() => ({ data: { total: 0, active: 0, rooms_total: 0 } }))
          : empty({ total: 0, active: 0, rooms_total: 0 }),
        canFamilies
          ? api.get('/families/stats').catch(() => ({ data: { total_families: 0 } }))
          : empty({ total_families: 0 }),
      ]);

      const todayEntries = (todayPlanRes.data?.entries || []).filter((e) => !e.cancelled);

      setStats({
        students: studentsRes.data.with_enrollment || 0,
        studentsTotal: studentsRes.data.total || 0,
        teachers: teachersRes.data.active || kpisRes.data.teachers_active || 0,
        teachersTotal: teachersRes.data.total || kpisRes.data.teachers_total || 0,
        courses: kpisRes.data.courses_with_enrollment || coursesRes.data.active || 0,
        coursesTotal: coursesRes.data.active || kpisRes.data.courses_active || coursesRes.data.total || 0,
        locations: locationsRes.data.active || 0,
        locationsTotal: locationsRes.data.total || 0,
        rooms: locationsRes.data.rooms_total || 0,
        families: familiesRes.data.total_families || 0,
        enrollments: kpisRes.data.enrollments_active || 0,
        openInvoices: invoicesRes.data.pending_count || kpisRes.data.billing?.invoices_open || 0,
        pendingEnrollments: 0,
        todayLessons: todayEntries.length,
        conflicts: 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Get displayed values based on filter
  const displayStudents = showActiveOnly ? stats.students : stats.studentsTotal;
  const displayTeachers = showActiveOnly ? stats.teachers : stats.teachersTotal;
  const displayCourses = showActiveOnly ? stats.courses : stats.coursesTotal;
  const displayLocations = showActiveOnly ? stats.locations : stats.locationsTotal;
  const fmtBadge = (n) => (n > 0 ? n.toLocaleString('de-DE') : null);

  const ownTeachingUrl = `/unterrichtsverwaltung?teacher_id=${stats.teacherId || user?.teacher_id || ''}`;
  const tileDefs = {
    tile_unterrichtsverwaltung: !isTeacher && A('enrollments') && (
      <WidgetButton
        icon={Laptop}
        label={isTeacher ? 'Meine Belegungen' : 'Unterrichtsverwaltung'}
        onClick={() => navigate(isTeacher ? ownTeachingUrl : '/unterrichtsverwaltung')}
        description={isTeacher ? 'Eigene Schüler und Belegungen' : 'Belegungen bearbeiten'}
      />
    ),
    tile_kalender: (
      <WidgetButton icon={Calendar} label="Kalender" onClick={() => navigate('/calendar')} />
    ),
    tile_heutige_termine: A('lessons') && (
      <WidgetButton icon={Clock} label="Heutige Termine" onClick={openTodayCalendar} badge={stats.todayLessons > 0 ? stats.todayLessons : null} badgeColor="red" badgeOnClick={openTodayCalendar} badgeTitle="Heutige Termine im Kalender öffnen" />
    ),
    tile_anwesenheit: isTeacher && canLessons && (
      <WidgetButton icon={CheckSquare} label="Anwesenheit" onClick={() => navigate('/anwesenheitslisten')} />
    ),
    tile_reports: A('reports') && (
      <WidgetButton icon={TrendingUp} label={isTeacher ? 'Meine Reports' : 'Reports'} onClick={() => navigate('/reports')} />
    ),
    tile_suche: A('students') && (
      <WidgetButton icon={Search} label="Suche" onClick={() => navigate('/students')} />
    ),
    tile_app: showAdminDashboardWidgets && (
      <WidgetButton icon={Smartphone} label="App" onClick={() => navigate('/app-verwaltung')} description="App-Verwaltung" />
    ),
    tile_schueler: A('students') && (
      <WidgetButton icon={Users} label={isTeacher ? 'Meine Schüler' : 'Schüler'} onClick={() => navigate('/students')} badge={fmtBadge(displayStudents)} badgeColor="green" badgeOnClick={() => navigate(showActiveOnly ? '/students?is_active=true' : '/students')} badgeTitle={showActiveOnly ? 'Nur aktive Schüler anzeigen' : 'Alle Schüler anzeigen'} />
    ),
    tile_lehrkraefte: !isTeacher && A('teachers') && (
      <WidgetButton icon={GraduationCap} label="Lehrkräfte" onClick={() => navigate('/teachers')} badge={fmtBadge(displayTeachers)} badgeColor="green" badgeOnClick={() => navigate(showActiveOnly ? '/teachers?is_active=true' : '/teachers')} badgeTitle={showActiveOnly ? 'Nur aktive Lehrkräfte anzeigen' : 'Alle Lehrkräfte anzeigen'} />
    ),
    tile_kurse: A('courses') && (
      <WidgetButton icon={BookOpen} label={isTeacher ? 'Meine Kurse' : 'Kurse'} onClick={() => navigate('/courses')} badge={fmtBadge(displayCourses)} badgeColor="green" badgeOnClick={() => navigate(showActiveOnly ? '/courses?has_enrollment=true' : '/courses')} badgeTitle={showActiveOnly ? 'Nur belegte Kurse anzeigen' : 'Alle Kurse anzeigen'} />
    ),
    tile_standorte: !isTeacher && A('locations') && (
      <WidgetButton icon={MapPin} label="Standorte" onClick={() => navigate('/locations')} badge={fmtBadge(displayLocations)} badgeColor="green" badgeOnClick={() => navigate('/locations')} badgeTitle={showActiveOnly ? 'Aktive Standorte' : 'Alle Standorte'} />
    ),
    tile_raeume: !isTeacher && A('locations') && (
      <WidgetButton icon={DoorOpen} label="Räume" onClick={() => navigate('/locations')} badge={fmtBadge(stats.rooms)} badgeColor="green" badgeOnClick={() => navigate('/locations')} badgeTitle="Anzahl Räume" />
    ),
    tile_familien: !isTeacher && A('families') && (
      <WidgetButton icon={UsersRound} label="Familien" onClick={() => navigate('/families')} description="Geschwister" badge={fmtBadge(stats.families)} badgeColor="green" badgeOnClick={() => navigate('/families')} badgeTitle="Anzahl Familien" />
    ),
    tile_gebuehrenverwaltung: !isTeacher && (A('tariffs') || A('discounts')) && (
      <WidgetButton icon={Euro} label="Gebührenverwaltung" onClick={() => navigate('/tariffs-discounts')} />
    ),
    tile_neue_belegung: !isTeacher && A('enrollments') && (
      <WidgetButton icon={UserPlus} label="Neue Belegung" onClick={() => navigate('/courses')} />
    ),
    tile_rechnungen: !isTeacher && A('invoices') && (
      <WidgetButton icon={Receipt} label="Rechnungen" onClick={() => navigate('/invoices')} badge={stats.openInvoices > 0 ? stats.openInvoices : null} badgeColor="red" badgeOnClick={() => navigate('/invoices?status=open')} badgeTitle="Offene Rechnungen filtern" />
    ),
    tile_rechnungslauf: !isTeacher && A('billing') && (
      <WidgetButton icon={Euro} label="Rechnungslauf" onClick={() => navigate('/invoices')} description="Monatsabrechnung" />
    ),
    tile_sepa: !isTeacher && A('sepa') && (
      <WidgetButton icon={CreditCard} label="SEPA-Lastschrift" onClick={() => navigate('/invoices')} description="(Geplant)" />
    ),
    tile_export_csv: !isTeacher && A('reports') && (
      <WidgetButton icon={Download} label="Export CSV" onClick={() => navigate('/reports')} />
    ),
    tile_export_xlsx: !isTeacher && A('reports') && (
      <WidgetButton icon={FileSpreadsheet} label="Export XLSX" onClick={() => navigate('/reports')} />
    ),
    tile_msv_import: !isTeacher && A('imports') && (
      <WidgetButton icon={Upload} label="MSV Import" onClick={() => navigate('/msv-import')} />
    ),
    tile_import_konflikte: !isTeacher && A('imports') && (
      <WidgetButton icon={AlertCircle} label="Import-Konflikte" onClick={() => navigate('/msv-import')} badge={stats.conflicts > 0 ? stats.conflicts : null} badgeColor="red" badgeOnClick={() => navigate('/msv-import?tab=conflicts')} badgeTitle="Direkt zu offenen Import-Konflikten" />
    ),
    tile_einstellungen: !isTeacher && A('settings') && (
      <WidgetButton icon={Settings} label="Einstellungen" onClick={() => navigate('/settings')} />
    ),
    tile_meine_funktionen: !isTeacher && (
      <WidgetButton icon={Star} label="Meine Funktionen" onClick={() => navigate('/')} />
    ),
  };

  const renderSchnellzugriff = () => (
    <div>
      <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-1">Schnellzugriff</h2>
      {TILE_SECTION_META.map((sec) => {
        const keys = tileOrder.filter((k) => sec.keys.includes(k) && isVisible(k) && tileDefs[k]);
        if (keys.length === 0) return null;
        return (
          <WidgetSection key={sec.id} title={sec.title}>
            {keys.map((key) => (
              <SortableItem key={key} id={key} editMode={editMode} onMove={moveTile}>
                {tileDefs[key]}
              </SortableItem>
            ))}
          </WidgetSection>
        );
      })}
    </div>
  );

  const renderPageBlock = (id, homeBlocks) => {
    if (homeBlocks && Object.prototype.hasOwnProperty.call(homeBlocks, id)) {
      const node = homeBlocks[id];
      if (!node) return null;
      return (
        <SortableItem key={id} id={id} editMode={editMode} onMove={moveBlock} className="mb-5">
          {node}
        </SortableItem>
      );
    }
    if (id === 'filter_strip' && isVisible('filter_strip')) {
      return (
        <SortableItem key={id} id={id} editMode={editMode} onMove={moveBlock} className="mb-4">
          <div className="bg-white rounded-xl border border-slate-200 p-3 min-w-0 overflow-hidden flex flex-wrap gap-3 items-center justify-between">
            <Button
              onClick={() => setFooterSettings({ nurAktive: !footer.nurAktive })}
              variant={footer.nurAktive ? 'default' : 'outline'}
              size="sm"
              className={footer.nurAktive
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'border-slate-300 text-slate-600'}
              data-testid="filter-active-toggle"
            >
              {footer.nurAktive ? (
                <><Filter className="w-4 h-4 mr-2" /> Nur Aktive</>
              ) : (
                <><FilterX className="w-4 h-4 mr-2" /> Alle anzeigen</>
              )}
            </Button>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CalendarDays className="w-4 h-4" />
              <span>Aktiv ab: <strong>{stichtagLabel}</strong></span>
            </div>
          </div>
        </SortableItem>
      );
    }
    if (id === 'data_status' && permsLoaded && showAdminDashboardWidgets && isVisible('data_status')) {
      return (
        <SortableItem key={id} id={id} editMode={editMode} onMove={moveBlock} className="mb-4">
          <DataStatusWidget />
        </SortableItem>
      );
    }
    if (id === 'registrations_tracker' && permsLoaded && showRegistrationsTracker && isVisible('registrations_tracker')) {
      return (
        <SortableItem key={id} id={id} editMode={editMode} onMove={moveBlock} className="mb-4">
          <RegistrationsTrackerWidget />
        </SortableItem>
      );
    }
    if (id === 'sms_delivery' && permsLoaded && showAdminDashboardWidgets && isVisible('sms_delivery')) {
      return (
        <SortableItem key={id} id={id} editMode={editMode} onMove={moveBlock} className="mb-4">
          <SmsDeliveryWidget />
        </SortableItem>
      );
    }
    if (id === 'my_quicklinks' && isVisible('my_quicklinks') && quicklinks.length > 0) {
      return (
        <SortableItem key={id} id={id} editMode={editMode} onMove={moveBlock} className="mb-6">
          <div data-testid="start-quicklinks-section">
            <div className="flex items-center gap-2 mb-3 px-1">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Meine Quicklinks</h3>
              <button
                onClick={() => setQuicklinksOpen(true)}
                className="text-slate-400 hover:text-slate-700"
                title="Quicklinks bearbeiten"
                data-testid="start-quicklinks-edit-btn"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
              {quicklinks.map((l, idx) => (
                <WidgetButton key={`${l.href}-${idx}`} icon={Link2} label={l.label} onClick={() => navigate(l.href)} />
              ))}
            </div>
          </div>
        </SortableItem>
      );
    }
    if (id === 'schnellzugriff') {
      return (
        <SortableItem key={id} id={id} editMode={editMode} onMove={moveBlock} className="mb-4">
          {renderSchnellzugriff()}
        </SortableItem>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="p-4 sm:p-6 min-w-0 overflow-x-hidden">
          {editMode && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-brand-red/30 bg-brand-red/5 px-4 py-3" data-testid="startfenster-edit-banner">
              <p className="text-sm text-slate-700">
                <strong>Editiermodus:</strong> Blöcke und Kacheln am Griff ziehen. Position wird automatisch gespeichert.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await resetOrder();
                      toast.success('Reihenfolge zurückgesetzt');
                    } catch {
                      toast.error('Zurücksetzen fehlgeschlagen');
                    }
                  }}
                >
                  Standard-Reihenfolge
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-brand-red hover:bg-brand-red/90"
                  onClick={() => setEditMode(false)}
                  data-testid="startfenster-edit-done"
                >
                  Fertig
                </Button>
              </div>
            </div>
          )}

          <StartfensterCustomizeDialog
            open={customizeOpen}
            onOpenChange={setCustomizeOpen}
            hidden={hidden}
            onSave={saveStartfenster}
          />

          <QuicklinksEditDialog
            open={quicklinksOpen}
            onOpenChange={setQuicklinksOpen}
            quicklinks={quicklinks}
            onSave={saveQuicklinks}
          />

          {permsLoaded ? (
            <HomeOpsBoard
              canStudents={canStudents}
              canEnrollments={canEnrollments}
              canLessons={canLessons}
              canReports={canReports}
              canCourses={canCourses}
              canMessages={A('messages')}
              isAdminish={isAdminish}
              showDataQuality={showDataQuality}
              isTeacher={isTeacher}
              footerParams={footerToParams(footer)}
              showActiveOnly={showActiveOnly}
              isVisible={isVisible}
              slot={({ welcome, blocks }) => (
                <div className="mb-8" data-testid="msv-home-board">
                  {welcome}
                  {order.map((id) => renderPageBlock(id, blocks))}
                </div>
              )}
            />
          ) : null}

          {/* Footer Bar */}
          <div className="bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">
                MSV Musikschulverwaltung v1.0
              </span>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-xs text-slate-600">
                Aktiv ab: <strong>{stichtagLabel}</strong>
              </span>
              {!isTeacher && <button
                type="button"
                onClick={() => setEditMode((v) => !v)}
                className={`flex items-center gap-1 text-xs ${editMode ? 'text-brand-red font-semibold' : 'text-slate-600 hover:text-slate-900'}`}
                data-testid="startfenster-edit-btn"
                title="Blöcke verschieben"
              >
                <Move className="w-4 h-4" />
                {editMode ? 'Anordnen beenden' : 'Anordnen'}
              </button>}
              {!isTeacher && <button
                type="button"
                onClick={() => setCustomizeOpen(true)}
                className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                data-testid="startfenster-customize-btn"
                title="Startfenster anpassen"
              >
                <LayoutGrid className="w-4 h-4" />
                Startfenster anpassen
              </button>}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
